import { externalEvents, roomEvents } from "@closerplatform/closer-sdk";
import { ID } from "@closerplatform/closer-sdk/dist/protocol/protocol";
import { observable } from "mobx";
import {
  AppPresence,
  LeadCtx,
  LimitedAgentProfile,
  Organization,
  OrgConfig
} from "@closerplatform/spinner-openapi";
import { markLastMessage, Marks } from "@fidget/common/closer";
import { staticConfig } from "@fidget/common/config/static-config";
import { intl } from "@fidget/common/i18n/TypedIntl";
import {
  ChatItemStatus,
  FormPayload,
  IMessageItem,
  MeetingSchedulerChatItem,
  UserTextChatItem
} from "@fidget/common/models/chat/chatItems";
import { Language } from "@fidget/common/models/language";
import { MeetingSchedulerStatus } from "@fidget/common/models/meeting-scheduler-status";
import { MessageTag } from "@fidget/common/models/messageTag";
import { NotificationType } from "@fidget/common/models/mst/ui/ChatNotificationStore";
import { ShortOrgConfig } from "@fidget/common/models/shortOrgConfig";
import { SharedPopupState } from "@fidget/common/store/sharedPopupStore";
import { LogLevel, logger, noop } from "@fidget/common/utils";
import { setOverrideStyles, setOrgColor } from "@fidget/common/utils/setOrgColor";
import { takeRightWhile, takeWhile } from "lodash";
import { action } from "mobx";
import { ForeContext } from "../context";
import { IForeRootStore } from "../models";
import { IForeCallStore } from "../models/call/foreCallModel";
import { ForeTypingStore } from "../models/typing/foreTypingStore";
import { AuthErrors } from "../services/authService";
import { MESSAGE_FILTERS } from "./common";
import { envActions } from "./env";
import { hardcodedCloserBotProfile } from "@fidget/common/models/hacks";

export class ApiActions {
  typingStore: ForeTypingStore = new ForeTypingStore();

  // TODO: after all refactorings are done, the stores will not be accessible from services.
  // Keep in mind that having here the reference to stores is only temporary solution

  @action.bound
  agentProfilesLoaded(sharedState: SharedPopupState, profiles: Array<LimitedAgentProfile>) {
    profiles.forEach((profile) => sharedState.agents.set(profile.id, profile));
  }

  @action.bound
  agentProfileUpdated(sharedState: SharedPopupState, profile: LimitedAgentProfile) {
    const agent = sharedState.agents.get(profile.id);
    Object.assign(agent, profile);
  }

  @action.bound
  assignedAgentLoaded(foreContext: ForeContext, assignedAgent?: LimitedAgentProfile) {
    foreContext.state.shared.assignedAgent = assignedAgent;
  }

  @action.bound
  async guestLoggedIn(
    foreContext: ForeContext,
    foreStore: IForeRootStore,
    profile: LeadCtx,
    apiKey: string
  ) {

    logger.setUserLogContext({ orgId: profile.orgId, userId: profile.id, roomId: profile.roomId });

    foreContext.state.profile = profile;
    foreContext.state.shared.roomId = profile.roomId;
    foreContext.hostWindowRequests.onGetCredentials(async () => ({ apiKey, id: profile.id }));

    await foreContext.fidgetService
      .getConfig()
      .then((config) => this.orgRetrieved(foreContext, config.org))
      .then(() => this.botRetrieved(foreContext))
      .catch((err) => logger.error(err));

    const { fingerprint } = foreStore.fingerprintStore;
    if (fingerprint) {
      foreContext.artichokeService.connect(profile.id, apiKey, { fingerprint });
    } else {
      logger.error("Fingerprint hasn't been generated while connecting to artichoke");
    }
    this.subscribeToArtichokeEvents(foreContext, foreStore);
  }

  @action.bound
  cobrowsingEnded(foreContext: ForeContext) {
    foreContext.hostWindowRequests.cobrowsingSessionEnded();
  }

  private subscribeToArtichokeEvents(foreContext: ForeContext, foreStore: IForeRootStore) {
    foreContext.artichokeService.onConnected(() => {
      const { profile } = foreContext.state;
      if (!profile) {
        return;
      }

      this.registerChatHandlers(foreContext, foreStore.foreCallStore, profile.roomId);

      Promise.all([
        foreContext.spinnerService.getAssignee(profile.id),
        foreContext.artichokeService.getMarks(profile.roomId),
        foreContext.artichokeService.latestMessages(profile.roomId, staticConfig.widgetPageSize, {
          customFilter: MESSAGE_FILTERS,
          filter: [roomEvents.MessageSent.tag]
        }),
        foreContext.artichokeService.latestMessages(profile.roomId, 10, {
          customFilter: [MessageTag.MEETING],
          filter: []
        })
      ]).then(([assignee, marks, page, meetingSchedulerEvents]) => {
        if (assignee.assignee) {
          const limitedAssignee: LimitedAgentProfile = observable(assignee.assignee);
          this.agentProfilesLoaded(foreContext.state.shared, [limitedAssignee]);
          this.assignedAgentLoaded(foreContext, limitedAssignee);
        }
        this.chatInitialized(foreContext, marks, page);
        // if there are pending meetingEvents, set pendingMeetingToken from the last one
        const lastPendingMeeting = meetingSchedulerEvents
          .slice()
          .reverse()
          .find(
            (item) =>
              (item as roomEvents.CustomMessageSent).context.status ===
              MeetingSchedulerStatus.PENDING
          ) as roomEvents.CustomMessageSent;
        if (lastPendingMeeting) {
          this.meetingSchedulingStarted(foreContext, lastPendingMeeting.context.token);
        }

        foreContext.artichokeService.onDisconnected(() => {
          foreContext.state.room.chat.allPendingFailed();
        });

        foreContext.artichokeService.onCustomMessage((message: roomEvents.CustomMessageSent) => {
          if (message.subtag === MessageTag.MAXIMIZE_WIDGET) {
            foreContext.hostWindowRequests.maximizeWindow();
          } else if (message.subtag === MessageTag.LEKTA_MESSAGE) {
            if (message.context && message.context.lektaContext) {
              try {
                const data = JSON.parse(message.context.lektaContext)?.closer?.onLektaContext;
                if (data) {
                  foreContext.hostWindowRequests.lektaContext(data);
                }
              } catch (e) {
                logger.warn(`Bad lektaContext ${message.context.lektaContext}`);
              }
            }
          }
        });

        foreContext.artichokeService.onGuestProfileUpdated((ev) => {
          foreContext.handleTagGroupChange(ev.tagGroupId);
        });
        foreContext.artichokeService.onGuestProfileUpdated((ev) => {
          foreContext.handleTagsChange(ev.tags as string[]);
        });
        foreContext.artichokeService.onConversationStatusChanged((ev) => {
          foreContext.hostWindowRequests.conversationStatusChanged(ev.status);
        });
      });
    });

    foreContext.artichokeService.onPresenceUpdated((event: externalEvents.PresenceUpdated) => {
      this.onPresenceUpdated(foreContext, event);
    });

    foreContext.artichokeService.onUnauthorized(() => {
      envActions.handleFailedInitialization(foreContext, foreStore, AuthErrors.OAuthError);
      foreContext.artichokeService.disconnect();
      foreStore.uiStore.removeChatNotificationIfType(NotificationType.NoConnection);
    });
  }

  @action.bound
  orgConfigRetrieved(foreContext: ForeContext, orgConfig: OrgConfig) {
    const {
      name,
      logoToken,
      color,
      overrideStyles,
      coverToken,
      callsDisabled,
      cobrowseEnabled,
      agreement,
      typingPreviewEnabled,
      forgetMeEnabled,
      continueConversationOnMobileEnabled,
      showAdviserLastNameEnabled,
      profanitiesCheckingEnabled,
      keyboardShortcutsEnabled,
      allowedAttachmentExtensions,
      maximumAttachmentSizeBytes,
      messageMaxAllowedCharactersForClient,
      phoneRegion,
      webSpeechApiEnabled,
      widgetLogLevel,
    } = orgConfig;
    if (color) {
      setOrgColor(color);
    }
    if (overrideStyles) {
      setOverrideStyles(overrideStyles);
    }
    const language = foreContext.language;

    logger.setLevel(LogLevel[widgetLogLevel as keyof typeof LogLevel]);

    foreContext.state.shared.orgConfig = ShortOrgConfig.create({
      name,
      headerWelcomeMessage: this.getHeaderWelcomeTextFromConfig(orgConfig, language),
      welcomeMessage: this.getWelcomeTextFromConfig(orgConfig, language),
      maintenanceWindows: orgConfig.maintenanceWindows,
      logoToken,
      color,
      coverToken,
      callsDisabled,
      agreement,
      typingPreviewEnabled,
      cobrowseEnabled,
      forgetMeEnabled,
      continueConversationOnMobileEnabled,
      showAdviserLastNameEnabled,
      profanitiesCheckingEnabled,
      keyboardShortcutsEnabled,
      allowedAttachmentExtensions,
      maximumAttachmentSizeBytes,
      messageMaxAllowedCharactersForClient,
      phoneRegion,
      webSpeechApiEnabled
    });
    foreContext.hostWindowRequests.sendOrgConfig(orgConfig);
  }

  @action.bound
  orgRetrieved(foreContext: ForeContext, org: Organization) {
    foreContext.state.shared.org.name = org.name;
    foreContext.state.shared.org.id = org.id;
  }

  @action.bound
  async botRetrieved(foreContext: ForeContext) {
    const orgId = foreContext.state.shared.org.id;
    const config = await foreContext.fidgetService.getConfig();
    foreContext.state.shared.agents.set(orgId, config.bot);
  }

  @action.bound
  chatInitialized(foreContext: ForeContext, marks: Marks, page: ReadonlyArray<roomEvents.RoomEvent>) {
    foreContext.state.room.marks = new Map(
      Object.keys(marks).map((key) => [key, marks[key]] as [ID, number])
    );
    this.chatPageLoaded(
      foreContext,
      page,
      page.length < staticConfig.widgetPageSize,
      page.length < staticConfig.widgetPageSize
    );
  }

  @action.bound
  chatPageLoaded(
    foreContext: ForeContext,
    page: ReadonlyArray<roomEvents.RoomEvent>,
    historyIsFullyLoaded = false,
    showWelcomeMessage = false,
  ) {
    const state = foreContext.state;
    state.room.isReloading = true;
    if (showWelcomeMessage) {
      this.getWelcomeMessage(foreContext).then((message) => state.room.chat.addCustomMessage(message)).catch(noop);
    }

    state.room.oldestTimestamp = page[0]?.timestamp;
    state.room.historyIsFullyLoaded = historyIsFullyLoaded;
    this.appendChatItems(foreContext, page).then(() => {
      state.room.isLoading = false;
    });
    const mark = state.room.marks.get(state.profile!.id);
    if (mark) {
      state.room.chat.setReadUntilMark(state.profile!.id, mark);
    }
    markLastMessage(foreContext, foreContext.state.profile!.roomId, state.room.chat.history);
    state.room.isReloading = false;
    if (foreContext.state.room.chat.pendingMessages.length > 0) {
      foreContext.state.room.chat.pendingMessages.forEach((msg) => this.messageReceived(foreContext, msg, false));
      foreContext.state.room.chat.pendingMessages = [];
    }
  }

  @action.bound
  private async appendChatItems(foreContext: ForeContext, page: ReadonlyArray<roomEvents.RoomEvent>) {
    const unloadedItems = this.filterUnloadedItems(foreContext, [...page]);
    if (unloadedItems.length) {
      return foreContext.state.room.chat.addArtichokeEvents(unloadedItems);
    }
  }

  private filterUnloadedItems(
    foreContext: ForeContext,
    items: roomEvents.RoomEvent[]
  ): roomEvents.RoomEvent[] {
    const oldestLoadedTimestamp = foreContext.state.room.chat.oldestTimestamp;
    const newestLoadedTimestamp = foreContext.state.room.chat.newestTimestamp;

    if (!oldestLoadedTimestamp || !newestLoadedTimestamp) {
      return items;
    }

    const newerItems = takeRightWhile(items, (item) => item.timestamp > newestLoadedTimestamp);
    const olderItems = takeWhile(items, (item) => item.timestamp < oldestLoadedTimestamp);

    return [...olderItems, ...newerItems];
  }

  @action.bound
  onPresenceUpdated(foreContext: ForeContext, presenceUpdated: externalEvents.PresenceUpdated) {
    const agent = foreContext.state.shared.agents.get(presenceUpdated.userId);
    const assignedAgent = foreContext.state.shared.assignedAgent;
    if (agent) {
      foreContext.state.shared.agents.set(presenceUpdated.userId, {
        ...agent,
        presence: presenceUpdated.presence as unknown as AppPresence
      });
    }
    if (assignedAgent && assignedAgent.id === presenceUpdated.userId) {
      assignedAgent.presence = presenceUpdated.presence as unknown as AppPresence;
    }
  }

  @action.bound
  assigneeChanged(foreContext: ForeContext, assigneeId: string) {
    const newAgent = foreContext.state.shared.agents.get(assigneeId);
    if (newAgent) {
      foreContext.state.shared.assignedAgent = newAgent;
    } else {
      foreContext.spinnerService.getAgentProfile(assigneeId).then((profile) => {
        this.agentProfilesLoaded(foreContext.state.shared, [profile]);
        foreContext.state.shared.assignedAgent = profile;
      });
    }
  }

  @action.bound
  assigneeRemoved(foreContext: ForeContext) {
    foreContext.state.shared.assignedAgent = undefined;
  }

  registerChatHandlers(foreContext: ForeContext, callStore: IForeCallStore, roomId: ID) {
    const { artichokeService, state } = foreContext;

    artichokeService.onRoomMessage(roomId, (message) =>
      this.messageReceived(foreContext, message, callStore.isNoCall)
    );
    artichokeService.onRoomMessageUpdated(roomId, (message) =>
      this.messageUpdated(foreContext, message)
    );
    artichokeService.onRoomMarked(roomId, (mark) => this.markReceived(foreContext, mark));
    artichokeService.onRoomTyping(roomId, (typing) =>
      this.chatTypingReceived(foreContext, typing.authorId)
    );

    MESSAGE_FILTERS.forEach((tag) =>
      artichokeService.onCustom(roomId, tag, (message) =>
        this.messageReceived(foreContext, message, callStore.isNoCall)
      )
    );

    artichokeService.onCustom(
      roomId,
      MessageTag.ASSIGNEE_CHANGED,
      (event: roomEvents.CustomMessageSent) => {
        const { assignee }: { assignee: string; } = event.context;
        if (assignee) {
          this.assigneeChanged(foreContext, assignee);
        }
      }
    );

    artichokeService.onCustom(
      roomId,
      MessageTag.ASSIGNEE_REMOVED,
      (event: roomEvents.CustomMessageSent) => {
        this.assigneeRemoved(foreContext);
      }
    );

    artichokeService.onCustom(roomId, MessageTag.MEETING, (event: roomEvents.CustomMessageSent) => {
      const { token, status } = event.context;
      switch (status) {
        case MeetingSchedulerStatus.PENDING:
          return this.meetingSchedulingStarted(foreContext, token);
        case MeetingSchedulerStatus.CANCELLED:
          return this.meetingSchedulingCancelled(foreContext, token);
        default:
          break;
      }
    });

    artichokeService.onCustom(roomId, MessageTag.COBROWSING_ENDED, () => {
      this.cobrowsingEnded(foreContext);
    });

    artichokeService.onCustom(roomId, MessageTag.FORM_SUBMITTED, (e) => {
      const { formId } = e.context as FormPayload;

      state.room.chat.reloadSubmission(formId);
    });

    artichokeService.onRoomMessageUpdated(roomId, (event: roomEvents.MessageUpdated) => {
      const item = foreContext.state.room.chat.getItemById(event.messageId);
      if (item && MeetingSchedulerChatItem.isMeetingSchedulerChatItem(item)) {
        if (event.context.status === MeetingSchedulerStatus.CANCELLED) {
          return this.meetingSchedulingCancelled(foreContext, event.context.token);
        }
      }
    });
  }

  @action.bound
  meetingSchedulingStarted(foreContext: ForeContext, token: string) {
    foreContext.state.pendingMeetingToken = token;
  }

  @action.bound
  meetingSchedulingCancelled(foreContext: ForeContext, token: string) {
    const { state } = foreContext;
    if (state.pendingMeetingToken && state.pendingMeetingToken === token) {
      state.pendingMeetingToken = undefined;
    }
  }

  @action.bound
  messageReceived(
    foreContext: ForeContext,
    msg: roomEvents.MessageSent | roomEvents.CustomMessageSent,
    isNoCall: boolean
  ) {
    if (foreContext.state.room.isReloading) {
      foreContext.state.room.chat.pendingMessages.push(msg);
      return;
    }
    foreContext.hostWindowRequests.messageReceived();
    foreContext.artichokeService.setDelivery(msg.roomId, msg.messageId);

    const state = foreContext.state;
    const { usersTyping } = foreContext.state.room;
    const own = state.profile && state.profile.id === msg.authorId;
    const isBot = msg.authorId === this.getBotProfile(foreContext)?.id;

    if (foreContext.state.shared.orgConfig?.webSpeechApiEnabled && isBot) {
      this.handleSpeechSynthesis(msg, foreContext.language);
    }

    state.room.chat.addArtichokeEvent(msg);
    this.removeTypings(usersTyping, msg.authorId);
    // TODO: #artichoke-refactor
    // msg.onDelivery(() => markItemAsDelivered(context.state.room.history, item.id));
    if (!own && !state.chatIsVisible) {
      const agent = foreContext.state.shared.agents.get(msg.authorId);
      const text = roomEvents.CustomMessageSent.isCustomMessageSent(msg)
        ? msg.context.text
        : msg.message;
      if (text) {
        foreContext.hostWindowRequests.sendIncomingMessage({
          text,
          avatarToken: agent ? agent.avatarToken : undefined,
          avatarFirstName: agent && agent.firstName
        });
      }
    }

    if ((state.chatScrolledToBottom && (state.tabIsActive || state.widgetIsActive) && state.chatIsVisible) || own) {
      foreContext.artichokeService.setMark(msg.roomId, msg.timestamp);
      foreContext.hostWindowRequests.setUnreadMessages(false);
    } else {
      foreContext.hostWindowRequests.setUnreadMessages(true);
    }

    const shouldPlayMessageSound = roomEvents.CustomMessageSent.isCustomMessageSent(msg) ? (() => {
      switch (msg.subtag) {
        case MessageTag.LEKTA_MESSAGE: {
          return msg.context.message || msg.message;
        }
        case MessageTag.FILE_DOWNLOADED:
        case MessageTag.LINK_CLICKED:
        case MessageTag.COBROWSING_ENDED:
        case MessageTag.ALL_FOLLOWERS_REMOVED: {
          return false;
        }
        default: {
          return true;
        }
      }
    })() : true;
    if (isNoCall && !own && !(state.chatIsVisible && state.tabIsActive) && shouldPlayMessageSound) {
      foreContext.ringtoneService.playMessageSound();
    }
  }

  @action.bound
  messageUpdated(foreContext: ForeContext, msg: roomEvents.MessageUpdated) {
    foreContext.state.room.chat.updateMessage(msg.messageId, msg);
  }

  @action.bound
  markReceived(foreContext: ForeContext, mark: roomEvents.MarkSent) {
    const state = foreContext.state;
    state.room.marks.set(mark.authorId, mark.timestamp);
    if (state.profile && state.profile.id !== mark.authorId) {
      state.room.chat.setReadUntilMark(state.profile.id, mark.timestamp);
    }
  }

  @action.bound
  chatTypingReceived(foreContext: ForeContext, userId: ID) {
    const { usersTyping } = foreContext.state.room;
    this.typingStore.updateTypingUser(
      userId,
      (id: ID) => usersTyping.includes(id) || usersTyping.push(id),
      (id: ID) => usersTyping.includes(id) && this.removeTypings(usersTyping, id)
    );
  }

  private handleSpeechSynthesis(
    msg: roomEvents.MessageSent | roomEvents.CustomMessageSent,
    lang: Language
  ): void {
    try {
      const utt = new SpeechSynthesisUtterance(msg.message);
      utt.lang = lang;
      speechSynthesis.cancel();
      speechSynthesis.speak(utt);
    } catch (e) {
      logger.warn("SpeechSynthesis is enabled but not supported in this context", e);
    }
  }

  private getWelcomeTextFromConfig(orgConfig: OrgConfig, lang: Language): string | undefined {
    if (!orgConfig.welcomeMessageEnabled) {
      return undefined;
    }

    if (orgConfig.welcomeMessagePl && orgConfig.welcomeMessageEn) {
      return lang === Language.PL ? orgConfig.welcomeMessagePl : orgConfig.welcomeMessageEn;
    } else if (orgConfig.welcomeMessageEn) {
      return orgConfig.welcomeMessageEn;
    } else if (orgConfig.welcomeMessagePl) {
      return orgConfig.welcomeMessagePl;
    } else {
      return undefined;
    }
  }

  private getHeaderWelcomeTextFromConfig(orgConfig: OrgConfig, lang: Language): string | undefined {
    if (orgConfig.headerWelcomeMessagePl && orgConfig.headerWelcomeMessageEn) {
      return lang === Language.PL
        ? orgConfig.headerWelcomeMessagePl
        : orgConfig.headerWelcomeMessageEn;
    } else if (orgConfig.headerWelcomeMessageEn) {
      return orgConfig.headerWelcomeMessageEn;
    } else if (orgConfig.headerWelcomeMessagePl) {
      return orgConfig.headerWelcomeMessagePl;
    } else {
      return undefined;
    }
  }

  private getBotProfile(foreContext: ForeContext): LimitedAgentProfile | undefined {
    const shared = foreContext.state.shared;
    return shared.agents.get(shared.org.id);
  }

  private getWelcomeMessage(foreContext: ForeContext): Promise<IMessageItem> {
    const state = foreContext.state;
    const messageFromConfig = state.shared.orgConfig && state.shared.orgConfig.welcomeMessage;

    // disable showing defaultMessage
    if (!messageFromConfig) {
      return Promise.reject();
    }

    const orgName = state.shared.org.name;
    const defaultMessage = intl.formatMessage("widget.chat.welcomeMessage", {
      companyName: orgName
    });
    const welcomeText = messageFromConfig || defaultMessage;
    const botProfile = this.getBotProfile(foreContext);

    if (botProfile === undefined) {
      throw new Error("could not find bot");
    }

    const closerBotProfile = {
      ...botProfile,
      ...hardcodedCloserBotProfile,
    };

    return Promise.resolve(new UserTextChatItem("welcome-message", 0, welcomeText, closerBotProfile, ChatItemStatus.SENT));
  }

  private removeTypings(usersTyping: string[], userId: ID): string[] {
    return usersTyping.splice(usersTyping.indexOf(userId), 1);
  }
}
